import cx from "classnames";

import { appstr } from "@coreapp/utils/appstr";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { GetFieldLabelFromFieldName } from "../../Composites/CompositeChartSearch/AdvancedSearchModal";
import { Menu, OverflowText, Switch, Text } from "@theorgchartdev/storybook";
import { GetFieldDefFromName } from "@/Utils/DataLib";

export const SwitchWrapper = ({ checked, onChange, option, disabled, tooltipProps }) => {
  const handleSwitchChange = event => {
    onChange(option, checked);
    event.stopPropagation();
  };
  return (
    <Switch
      checked={checked}
      onChange={handleSwitchChange}
      disabled={disabled}
      tooltipProps={tooltipProps}
    />
  );
};

const BuildFieldOptions = (
  selectedNames = [],
  fieldNames,
  onChange,
  small,
  dataTestIdPrefix,
  localize = false,
  useFieldDefs = false,
  disabledFieldIds = [],
  getHoverTipContent = undefined,
  localizationMap
) => {
  let fieldOptions = [];
  let baseClass = "ocn-field-toggle-option";
  let textSize = small ? "xsmall" : "small";
  const disabledFieldIdSet = new Set(disabledFieldIds);

  const fieldMetaData = fieldNames.map(fieldName => {
    if (useFieldDefs && typeof fieldName === "object" && fieldName) {
      const fieldID = fieldName.GetFieldID();
      const fName = fieldName.GetFieldName();
      return {
        fieldName,
        fieldLabel:
          localizationMap && fieldID in localizationMap
            ? localizationMap[fieldID]
            : localize
              ? appstr(fName)
              : fieldName.GetFieldLabel(),
        disabled: disabledFieldIdSet.has(fieldID)
      };
    }
    return {
      fieldLabel: localize ? appstr(fieldName) : GetFieldLabelFromFieldName(fieldName),
      fieldName,
      disabled: disabledFieldIdSet.has(fieldName)
    };
  });
  fieldMetaData.sort((a, b) => {
    if (a.disabled !== b.disabled) {
      return a.disabled ? -1 : 1;
    }
    return a.fieldLabel.localeCompare(b.fieldLabel);
  });

  for (const { fieldLabel, fieldName, disabled } of fieldMetaData) {
    const fieldNameToConsider =
      typeof fieldName === "object" && fieldName ? fieldName.GetFieldName() : fieldName;
    let checked = selectedNames.includes(fieldNameToConsider);
    let checkedClass = checked && `${baseClass}-label--checked`;
    let fieldOption = {};
    fieldOption.content = (
      <span className={baseClass}>
        <span className={cx(`${baseClass}-label`, checkedClass)}>
          <OverflowText size={textSize}>{fieldLabel}</OverflowText>
        </span>
        <span className={`${baseClass}-switch`}>
          <SwitchWrapper
            id={"directory-column-fields-" + fieldNameToConsider}
            checked={checked}
            option={fieldOption}
            onChange={onChange}
            disabled={disabled}
            tooltipProps={
              disabled
                ? {
                    content: getHoverTipContent
                      ? getHoverTipContent(fieldName)
                      : appstr("Core fields cannot be hidden in the table"),
                    position: "right-end"
                  }
                : undefined
            }
          />
        </span>
      </span>
    );
    if (dataTestIdPrefix) fieldOption.dataTestId = `${dataTestIdPrefix}${fieldLabel}`;
    // menu option callback passes back option obj which contains the value and callback fn handles extraction
    fieldOption.clickHandler = disabled ? undefined : onChange;
    fieldOption.disabled = disabled;
    fieldOption.enableHoverBackground = !disabled;
    fieldOption.className = disabled ? "ocn-field-toggle-option--disabled" : "";
    fieldOption.value = fieldName;
    fieldOption.label = fieldLabel;
    fieldOption.key = fieldLabel;
    fieldOptions.push(fieldOption);
  }
  return fieldOptions;
};

const FieldToggleMenu = ({
  selectedFieldNames,
  disabledFieldIds,
  fieldNames,
  onChange,
  small,
  dataTestIdPrefix,
  localize,
  useFieldDefs = false,
  getHoverTipContent,
  localizationMap = undefined,
  ...menuProps
}) => {
  let menuOptions = BuildFieldOptions(
    selectedFieldNames,
    fieldNames,
    onChange,
    small,
    dataTestIdPrefix,
    localize,
    useFieldDefs,
    disabledFieldIds,
    getHoverTipContent,
    localizationMap
  );

  const listWidth = menuProps?.listProps?.listWidth ?? "large";

  if (menuProps?.listProps?.enableSearch !== false) {
    menuProps = {
      ...menuProps,
      listProps: {
        ...menuProps?.listProps,
        ...FieldSelectListProps({
          customPlaceholder: menuProps?.listProps?.placeholder,
          customNoResultsText: menuProps?.listProps?.noResultsText,
          inputProps: menuProps?.listProps?.inputProps
        }),
        listWidth: listWidth
      }
    };
  }

  return <Menu options={menuOptions} stayOpenOnOptionSelect={true} {...menuProps} />;
};

export default FieldToggleMenu;
